import { NgZone } from '@angular/core'
import { BehaviorSubject, Subject, Subscription } from 'rxjs'
import { AuthService } from '@services/auth.service'
import { IMessage } from '@stomp/stompjs'
import { RealtimeChannel, RealtimeConnectionStatus, RealtimeHealthService } from '@services/realtime/realtime-health.service'
import { StompConnectionService, StompLease, StompLeaseSubscription } from '@services/socket/stomp-connection.service'

/**
 * Tópico STOMP de um processo eletrônico, `/topic/tenant.{tenantId}.{canal}.process.{processId}`, no padrão de
 * `notice-session-status-socket.service.ts`: assina pela conexão da aba (`StompConnectionService`), que renova o token
 * e refaz a assinatura a cada reconexão, e cada frame vira um evento dentro da zone. Quem estende diz o canal e como
 * ler o corpo.
 *
 * Sem conexão, a tela cobre com o `RealtimeFallbackPoller` (só com o canal fora) e re-sincroniza na reconexão.
 */
export abstract class ProcessTopicSocket<T> {
	private lease?: StompLease
	private leaseStatus?: Subscription
	private tenantId?: string
	private processId?: string
	private processSubscription?: StompLeaseSubscription
	private stream$ = new Subject<T>()
	private connectionStatus$ = new BehaviorSubject<RealtimeConnectionStatus>('disconnected')

	/** Canal no `RealtimeHealthService`. */
	protected abstract readonly channel: RealtimeChannel

	/** O trecho do destino entre o tenant e o processo, como o management publica (`electronic-process-books`). */
	protected abstract readonly topic: string

	/** O corpo do frame, já em JSON, como evento. */
	protected abstract parse(body: unknown): T

	protected constructor(
		protected health: RealtimeHealthService,
		protected authService: AuthService,
		protected stomp: StompConnectionService,
		protected zone: NgZone
	) {}

	get events$() {
		return this.stream$.asObservable()
	}

	get status$() {
		return this.connectionStatus$.asObservable()
	}

	get connected(): boolean {
		return this.connectionStatus$.value === 'connected'
	}

	connect(tenantId: string) {
		const token = this.authService.token
		if (!token || !tenantId) return

		if (this.lease && !this.lease.closed && this.tenantId === tenantId) return

		this.releaseLease()

		this.tenantId = tenantId
		this.lease = this.stomp.acquire({ tenantId })
		if (this.processId) this.subscribeProcess(this.processId)
		this.leaseStatus = this.lease.status$.subscribe(status => this.markStatus(status))
	}

	subscribeProcess(processId: string) {
		this.processId = processId
		if (!this.lease || this.lease.closed || !this.tenantId || !processId) return

		this.processSubscription?.unsubscribe()
		this.processSubscription = this.lease.subscribe(`/topic/tenant.${this.tenantId}.${this.topic}.process.${processId}`, frame => this.forward(frame))
	}

	/** Para de ouvir o processo, mantendo a conexão para o próximo. */
	unsubscribeProcess(processId?: string) {
		if (processId && this.processId !== processId) return

		this.processSubscription?.unsubscribe()
		this.processSubscription = undefined
		this.processId = undefined
	}

	private forward(frame: IMessage) {
		this.health.markFrame(this.channel)

		try {
			const event = this.parse(JSON.parse(frame.body))
			// Dentro da zone: sem isto o modelo muda e a tela não.
			this.zone.run(() => this.stream$.next(event))
		} catch {
			/* noop */
		}
	}

	async disconnect() {
		this.releaseLease()
		this.markStatus('disconnected')
	}

	/** A assinatura morre com a lease; o `processId` fica para o próximo `connect`. */
	private releaseLease() {
		this.processSubscription = undefined
		this.leaseStatus?.unsubscribe()
		this.leaseStatus = undefined
		this.lease?.release()
		this.lease = undefined
	}

	private markStatus(status: RealtimeConnectionStatus) {
		this.connectionStatus$.next(status)
		this.health.markStatus(this.channel, status)
	}
}
